import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ComponentState } from '@/runtime/types/componentState';

/**
 * Pinia store for managing component state and hierarchy.
 *
 * This store maintains a registry of all EConnect component instances and their relationships,
 * enabling parent-child lookups, type-based queries, and efficient prop updates.
 *
 * @template T - The component props type
 *
 * @example
 * ```typescript
 * const stateStore = useStateStore<MyComponentProps>();
 *
 * // Register a component
 * stateStore.setState('123', {
 *   uid: 123,
 *   type: 'my-component',
 *   parent: null,
 *   props: { value: 'hello' }
 * });
 *
 * // Update a prop
 * stateStore.setPropValue('123', 'value', 'world');
 *
 * // Find children of a specific type
 * const children = stateStore.findChildrenByType('123', 'child-component');
 * ```
 */
export const useStateStore = <T>() =>
  defineStore('storedProps', () => {
    const state = ref<Record<string, ComponentState<T>>>({});

    // Performance: Version counter for O(1) change detection (avoid Object.keys())
    let stateVersion = 0;

    // Performance: Cache for findChildrenByType to avoid repeated iterations
    const childrenCache = new Map<string, Map<string, Array<ComponentState<unknown>>>>();

    /**
     * Invalidates cached children for a component and its parent.
     *
     * @param uid - The component's unique identifier
     */
    const invalidateChildrenCache = (uid: string | number): void => {
      childrenCache.delete(String(uid));
      const node = getByUid(uid);
      if (node?.parent) {
        childrenCache.delete(String(node.parent));
      }
    };

    /**
     * Sets or updates the state for a component.
     *
     * If the component already exists, its properties are mutated to preserve object identity
     * and avoid breaking Vue reactivity. If it's a new component, it's added to the state.
     *
     * @param key - The component's unique identifier as a string
     * @param componentState - The complete component state to set
     */
    const setState = (key: string, componentState: ComponentState<T>): void => {
      const existing = state.value[key] as ComponentState<T> | undefined;
      if (existing) {
        // mutate to preserve identity
        existing.type = componentState.type;
        existing.parent = componentState.parent;
        if (existing.props) {
          Object.assign(existing.props, componentState.props);
        } else {
          existing.props = componentState.props;
        }
      } else {
        state.value[key] = componentState;
      }

      // Increment version to invalidate caches
      stateVersion++;

      // Invalidate cache when state changes
      invalidateChildrenCache(key);
    };

    /**
     * Finds the first component in state that matches a predicate function.
     *
     * @param pred - Predicate function to test each component
     * @returns The matching component state or null if not found
     */
    const findInState = (pred: (v: ComponentState<T>) => boolean): ComponentState<T> | null => {
      for (const v of Object.values(state.value)) {
        if (pred(v)) return v;
      }
      return null;
    };

    /**
     * Updates a single property value for a component.
     *
     * This is more efficient than calling setState for prop-only changes.
     *
     * @param uid - The component's unique identifier
     * @param property - The property name to update
     * @param value - The new value for the property
     */
    const setPropValue = <K extends keyof T>(
      uid: string | number,
      property: K,
      value: T[K]
    ): void => {
      if (state.value[uid]) {
        state.value[uid].props[property] = value;
      }
    };

    /**
     * Retrieves a component's state by its unique identifier.
     *
     * @param id - The component's unique identifier
     * @returns The component state or undefined if not found
     */
    const getByUid = (id: string | number): ComponentState<T> | undefined =>
      state.value[String(id)] as ComponentState<T> | undefined;

    /**
     * Finds the immediate parent of a component.
     *
     * @param uid - The component's unique identifier
     * @returns The parent's component state or undefined if no parent exists
     */
    const findParent = (uid: string | number): ComponentState<T> | undefined => {
      const current = getByUid(uid);
      if (!current?.parent) return undefined;
      return getByUid(current.parent);
    };

    /**
     * Finds the nearest ancestor of a specific type by walking up the component tree.
     *
     * @param uid - The component's unique identifier
     * @param type - The component type to search for (e.g., 'menu', 'layout')
     * @returns The ancestor's component state or undefined if not found
     */
    const findParentOfType = (
      uid: string | number,
      type: string
    ): ComponentState<T> | undefined => {
      const visited = new Set<string>();
      let node = findParent(uid);

      while (node) {
        const id = String(node.uid);
        if (visited.has(id)) break; // guard against cycles
        visited.add(id);

        // Check node.type property (standard ComponentState property)
        if (node.type === type) return node;

        if (!node.parent) return undefined;
        node = getByUid(node.parent);
      }
      return undefined;
    };

    /**
     * Finds all immediate children of a component.
     *
     * @param uid - The parent component's unique identifier
     * @returns Array of child component states
     */
    const findChildren = (uid: string | number): Array<ComponentState<T>> => {
      const u = String(uid);
      const result: Array<ComponentState<T>> = [];
      for (const v of Object.values(state.value)) {
        if (v?.parent != null && String(v.parent) === u) {
          result.push(v);
        }
      }
      return result;
    };

    /**
     * Finds all immediate children of a specific type.
     *
     * Results are cached for performance. The cache is invalidated when
     * the parent or any child state changes.
     *
     * @param uid - The parent component's unique identifier
     * @param type - The component type to filter by
     * @returns Array of matching child component states
     */
    const findChildrenByType = (uid: string | number, type: string): Array<ComponentState<T>> => {
      const u = String(uid);

      // Check cache first
      if (!childrenCache.has(u)) {
        childrenCache.set(u, new Map());
      }
      const typeCache = childrenCache.get(u)!;

      if (typeCache.has(type)) {
        return typeCache.get(type)! as Array<ComponentState<T>>;
      }

      // Not in cache, compute and cache it
      const children = findChildren(uid);
      const filtered = children.filter(child => child.type === type);
      typeCache.set(type, filtered as Array<ComponentState<unknown>>);

      return filtered;
    };

    /**
     * Clears all component state from the store.
     *
     * Preserves the state object identity to avoid breaking Vue watchers.
     * This is used for cleanup during Turbo navigation or testing.
     */
    const clear = (): void => {
      // preserve map identity to avoid breaking watchers
      const map = state.value;
      for (const k of Object.keys(map)) {
        Reflect.deleteProperty(map, k);
      }
      // Reset version counter
      stateVersion = 0;
      // Clear cache on store clear
      childrenCache.clear();
    };

    /**
     * Returns the current state version counter.
     *
     * The version increments on every state change, enabling O(1) change detection
     * without iterating over all state keys.
     *
     * @returns The current version number
     */
    const getVersion = (): number => stateVersion;

    /**
     * Removes a component's state from the store by its unique identifier.
     *
     * This invalidates any cached children relationships and cleans up
     * the component's entry completely.
     *
     * @param uid - The component's unique identifier
     * @returns true if the component was found and removed, false otherwise
     */
    const removeState = (uid: string | number): boolean => {
      const key = String(uid);

      if (!state.value[key]) {
        return false;
      }

      // Invalidate cache before removing
      invalidateChildrenCache(key);

      // Remove from state
      delete state.value[key];

      // Increment version
      stateVersion++;

      return true;
    };

    return {
      state,
      setState,
      setPropValue,
      removeState,
      findInState,
      getByUid,
      findParent,
      findParentOfType,
      findChildren,
      findChildrenByType,
      clear,
      getVersion
    };
  })();
